import React, { useEffect, useState } from "react";
import "../Styles/QuickContacts.css";
import {
  FaSearch,
  FaFilter,
  FaTrash,
  FaEnvelope,
  FaCommentDots,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { MdPhone } from "react-icons/md";

/* ✅ IMPORT API */
import ApiService from "../nhrc_apis/ApiService";

const { ContactAPI } = ApiService;

function QuickContacts() {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [viewContact, setViewContact] = useState(null);
  const [loading, setLoading] = useState(false);

  /* 🔹 LOAD FROM API */
  const fetchContacts = async () => {
    try {
      setLoading(true);

      const res = await ContactAPI.getAllContacts();

      const mappedData = (res.data || []).map((item, index) => ({
        id: item.id || item.contact_id || index + 1,
        fullName: item.name || "Unknown",
        email: item.email || "",
        phone: item.mobile || "",
        message: item.message || "",
        status: "Pending",
      }));

      setContacts(mappedData);
    } catch (err) {
      console.error(err);
      alert("❌ Failed to load contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  /* 🔹 SEARCH */
  const filteredContacts = contacts.filter((c) => {
    const term = searchTerm.toLowerCase();
    return (
      c.fullName.toLowerCase().includes(term) ||
      c.email.toLowerCase().includes(term) ||
      c.phone.toLowerCase().includes(term) ||
      c.message.toLowerCase().includes(term)
    );
  });

  /* 🔹 SORT */
  const sortedContacts = [...filteredContacts].sort((a, b) => {
    if (!sortOrder) return 0;
    return sortOrder === "asc"
      ? a.fullName.localeCompare(b.fullName)
      : b.fullName.localeCompare(a.fullName);
  });

  /* 🔹 SELECT */
  const toggleSelectContact = (id) => {
    setSelectedContacts((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    setSelectedContacts(
      e.target.checked ? sortedContacts.map((c) => c.id) : []
    );
  };

  /* 🔹 DELETE (API) */
  const handleDelete = async () => {
    if (selectedContacts.length === 0) {
      alert("Select at least one contact to delete!");
      return;
    }

    try {
      await Promise.all(
        selectedContacts.map((id) => ContactAPI.deleteContact(id))
      );

      alert("✅ Deleted successfully");

      setSelectedContacts([]);
      fetchContacts(); // reload
    } catch (err) {
      console.error(err);
      alert("❌ Delete failed");
    }
  };

  /* 🔹 SORT TOGGLE */
  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  /* 🔹 VIEW (GET BY ID) */
  const openView = async (contact) => {
    try {
      const res = await ContactAPI.getContactById(contact.id);

      const data = res.data;

      setViewContact({
        id: data.id,
        fullName: data.name,
        email: data.email,
        phone: data.mobile,
        message: data.message,
      });
    } catch (err) {
      console.error(err);
      alert("❌ Failed to fetch contact details");
    }
  };

  const closeView = () => setViewContact(null);

  return (
    <div className="quickcontacts-container">
      <div className="quickcontacts-header">
        <h1>QUICK CONTACTS</h1>
        <p className="summary fs-5">
          Total Contacts: <b>{contacts.length}</b>
        </p>
      </div>

      {/* 🔹 ACTION BAR */}
      <div className="quick-contact-actions">
        <div className="search-box">
          <FaSearch className="quick-search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, or message"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button className="filter-btn" onClick={handleSortToggle}>
          <FaFilter /> Sort{" "}
          {sortOrder === "asc" ? "(A-Z)" : sortOrder === "desc" ? "(Z-A)" : ""}
        </button>

        <button className="delete-btn" onClick={handleDelete}>
          <FaTrash /> Delete
        </button>
      </div>

      {/* 🔹 LIST */}
      <div className="quick-contact-list">
        <label>
          <input
            type="checkbox"
            className="quick-check"
            checked={
              selectedContacts.length === sortedContacts.length &&
              sortedContacts.length > 0
            }
            onChange={handleSelectAll}
          />{" "}
          <span className="quick-text">
            {sortedContacts.length} Contacts
          </span>
        </label>

        {loading ? (
          <p className="no-results">Loading...</p>
        ) : sortedContacts.length === 0 ? (
          <p className="no-results">No contacts found</p>
        ) : (
          sortedContacts.map((c) => (
            <div key={c.id} className="contact-card-quick">
              <input
                type="checkbox"
                className="quick-check"
                checked={selectedContacts.includes(c.id)}
                onChange={() => toggleSelectContact(c.id)}
              />

              <div className="avatar">
                {c.fullName.charAt(0).toUpperCase()}
              </div>

              <div className="details">
                <p className="info-name">
                  <FaUser /> {c.fullName}
                </p>

                <div className="d-flex">
                  <p className="info">
                    <FaEnvelope /> {c.email}
                  </p>
                  <p className="info ms-3">
                    <MdPhone size={20} /> {c.phone}
                  </p>
                </div>

                <p className="info message">
                  <FaCommentDots /> {c.message}
                </p>
              </div>

              <div className="status-section">
                <button className="view-btn" onClick={() => openView(c)}>
                  View
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 🔹 POPUP */}
      {viewContact && (
        <div className="popup-overlay-quick" onClick={closeView}>
          <div
            className="popup-box-quick"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-btn-quick" onClick={closeView}>
              <FaTimes />
            </button>

            <h3>Quick Contact › {viewContact.fullName}</h3>

            <div className="popup-card-quick">
              <div className="popup-avatar">
                {viewContact.fullName.charAt(0).toUpperCase()}
              </div>

              <div className="popup-fields">
                <label>Full Name</label>
                <input value={viewContact.fullName} readOnly />

                <label>Phone Number</label>
                <input value={viewContact.phone} readOnly />

                <label>Email</label>
                <input value={viewContact.email} readOnly />

                <label>Message</label>
                <textarea value={viewContact.message} rows="4" readOnly />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuickContacts;